import Axios, { AxiosResponse, AxiosError } from "axios"
import { NoteInfo } from "../types"
import { configureStore } from "stores/configureStore"
import { setPendingApprovalsAction } from "pages/auth/stores/actions"
import {
  PendingApproval,
  PendingApprovals,
  emptyPendingApprovals,
} from "pages/conversations/conversations.type"
import { EncryptionKeys } from "controllers/EncryptionController"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getPrivateKeyByUserId, replaceImagesWithText } from "helpers/utils"

export const createAgreementWithLastestDocument = async () => {
  return Axios.post(`/api/about/create-agreement`).then((res) => res.data)
}
export const getReleaseNotesMiddleware = async (): Promise<NoteInfo> => {
  return Axios.get(`api/change-log/get`).then((res) => res.data.data)
}
export const updateSeenNotesMiddleware = async () => {
  return Axios.put(`api/change-log/update-viewed-user`).then((res) => res.data)
}

export const getPendingApprovalsMiddleware = () => {
  Axios.get("/api/log/pending-approvals?loading=0")
    .then(async (response: AxiosResponse<PendingApprovals>) => {
      if (response?.data?.data) {
        if (Array.isArray(response.data.data)) {
          const userVaultKey = localStorage.getItem(
            EncryptionKeys.userVaultKey
          ) as string
          const newData: PendingApproval[] = []
          const projectEncryptionKeys = JSON.parse(
            localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
          )
          const privateKeyPem: any = await getPrivateKeyByUserId()
          const decryptRSA = (value?: string): string => {
            if (!value) return ''
            try {
              return privateKeyPem.decrypt(value, 'RSA-OAEP')
            } catch {
              return ''
            }
          }

          const decryptGCM = async (key: string, value?: string): Promise<string> => {
            if (!value) return ''
            try {
              return await encryptionGCMHelper.decryptContext(key, value)
            } catch {
              return ''
            }
          }
          for (const item of response.data.data) {
            let projectKey = ''

            // 🔐 Resolve project key
            if (item.key_encryption_project && !item.project_public_key) {
              projectKey = await decryptGCM(
                userVaultKey,
                item.key_encryption_project
              )
            } else {
              projectKey = decryptRSA(item.key_encryption_project)
            }
            if (!projectKey) {
              continue
            }
            projectEncryptionKeys[item.project_id] = projectKey
            // 🏗️ Build-level encryption
            if (item.key_encryption_build) {
              const buildKey = await decryptGCM(
                projectKey,
                item.key_encryption_build
              )
              if (buildKey && buildKey !== item.key_encryption_build) {
                const decryptedContent = await decryptGCM(buildKey, item.content)
                if (decryptedContent) {
                  newData.push({
                    ...item,
                    content: replaceImagesWithText(decryptedContent, [], true)
                  })
                }
              } else {
                const decryptedKey = decryptRSA(item.key_encryption_build)
                if (decryptedKey) {
                  const decryptedContent = await decryptGCM(decryptedKey, item.content)
                  if (decryptedContent) {
                    newData.push({
                      ...item,
                      content: replaceImagesWithText(decryptedContent, [], true)
                    })
                  }
                }

              }
            }

            // 💬 Conversation-level encryption
            if (item.key_encryption_conversation) {
              let conversationKey = ''

              if (!item.conversation_public_key) {
                conversationKey = await decryptGCM(
                  projectKey,
                  item.key_encryption_conversation
                )
                if (conversationKey === item.key_encryption_conversation) {
                  conversationKey = decryptRSA(item.key_encryption_conversation)
                }
              } else {
                conversationKey = decryptRSA(item.key_encryption_conversation)
              }
              if (
                conversationKey &&
                conversationKey !== item.key_encryption_conversation
              ) {
                const decryptedContent = await decryptGCM(
                  conversationKey,
                  item.content
                )

                if (decryptedContent) {
                  newData.push({
                    ...item,
                    content: replaceImagesWithText(decryptedContent, [], true)
                  })
                }
              }
            }
          }
          configureStore.dispatch(
            setPendingApprovalsAction({
              data: newData,
              total: newData.length,
            })
          )
          localStorage.setItem(
            EncryptionKeys.projectEncryptionKeys,
            JSON.stringify(projectEncryptionKeys)
          )
        } else {
          configureStore.dispatch(setPendingApprovalsAction(emptyPendingApprovals))
        }
      }
    })
    .catch((_error: AxiosError) => {
      configureStore.dispatch(setPendingApprovalsAction(emptyPendingApprovals))
    })
}
